/**
 * Centralized query definitions: the single source of truth for query
 * keys and fetchers, so components, cache updates, and route-loader
 * prefetching stay in sync instead of duplicating string keys.
 */
import { queryOptions } from "@tanstack/react-query";
import { api, type SongDetail } from "./api";

type SetlistListItem = {
  id: string;
  name: string;
  songs: Array<{ id: string }>;
  totalDurationSeconds: number;
};

/** A setlist entry: a Bandwith song, or (song null) just a title */
type SetlistSong = {
  id: string;
  songId: string | null;
  title: string | null;
  /** Chosen take for this setlist; null follows the song's primary recording */
  recordingId: string | null;
  position: number;
  song: {
    id: string;
    title: string;
    author?: {
      name: string;
    } | null;
    recordings?: Array<{
      id: string;
      filename: string;
      signedAudioUrl: string;
      position: number;
      durationSeconds: number;
    }>;
  } | null;
  primaryRecording?: {
    id: string;
    filename: string;
    signedAudioUrl: string;
    durationSeconds: number | null;
  } | null;
};

type SetlistDetailData = {
  id: string;
  name: string;
  notes?: string | null;
  unlisted?: boolean;
  createdBy?: {
    name: string;
  } | null;
  songs: SetlistSong[];
  totalDurationSeconds: number;
};

export const songsQuery = () =>
  queryOptions({
    queryKey: ["songs"] as const,
    queryFn: async () => {
      const res = await api.songs.$get();
      if (!res.ok) throw new Error("Failed to fetch songs");
      return res.json();
    },
  });

export const songQuery = (id: string) =>
  queryOptions({
    queryKey: ["song", id] as const,
    queryFn: async () => {
      const res = await api.songs[":id"].$get({ param: { id } });
      if (!res.ok) throw new Error("Failed to fetch song");
      return res.json() as Promise<SongDetail>;
    },
  });

export const songFeedbackQuery = (songId: string) =>
  queryOptions({
    queryKey: ["feedback", songId] as const,
    queryFn: async () => {
      const res = await api.feedback.song[":songId"].$get({
        param: { songId },
      });
      if (!res.ok) throw new Error("Failed to fetch feedback");
      return res.json();
    },
  });

export const setlistFeedbackQuery = (setlistId: string) =>
  queryOptions({
    queryKey: ["setlist-feedback", setlistId] as const,
    queryFn: async () => {
      const res = await api.feedback.setlist[":setlistId"].$get({
        param: { setlistId },
      });
      if (!res.ok) throw new Error("Failed to fetch feedback");
      return res.json();
    },
  });

export const setlistsQuery = () =>
  queryOptions({
    queryKey: ["setlists"] as const,
    queryFn: async () => {
      const res = await api.setlists.$get();
      if (!res.ok) throw new Error("Failed to fetch setlists");
      return res.json() as Promise<SetlistListItem[]>;
    },
  });

export const setlistQuery = (id: string) =>
  queryOptions({
    queryKey: ["setlist", id] as const,
    queryFn: async () => {
      const res = await api.setlists[":id"].$get({ param: { id } });
      if (!res.ok) throw new Error("Failed to fetch setlist");
      return res.json() as Promise<SetlistDetailData>;
    },
  });

export const musiciansQuery = () =>
  queryOptions({
    queryKey: ["musicians"] as const,
    queryFn: async () => {
      const res = await api.musicians.$get();
      if (!res.ok) throw new Error("Failed to fetch musicians");
      return res.json();
    },
  });
